import React from 'react';
import { RATING_EMOJIS } from '../../utils/constants';

function CreateReviewView({
  restaurantName,
  setRestaurantName,
  restaurantLocation,
  setRestaurantLocation,
  cuisineTypes,
  setCuisineTypes,
  description,
  setDescription,
  rating,
  setRating,
  insiderTips,
  setInsiderTips,
  visibility,
  setVisibility,
  photos,
  dishes,
  selectedPhotoIndex,
  setSelectedPhotoIndex,
  loading,
  onPhotoUpload,
  onRemovePhoto,
  onSelectPhoto,
  onDishChange,
  onSubmitReview
}) {
  return (
    <div className="space-y-6" data-testid="create-review-view">
      <h2 className="text-2xl font-heading font-bold">Share a spot you love</h2>
      
      <div>
        <label className="block text-sm font-semibold mb-2">Restaurant Name *</label>
        <input
          type="text"
          value={restaurantName}
          onChange={(e) => setRestaurantName(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-button focus:outline-none focus:border-primary"
          placeholder="Enter restaurant name"
          data-testid="restaurant-name-input"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Location *</label>
        <input
          type="text"
          value={restaurantLocation}
          onChange={(e) => setRestaurantLocation(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-button focus:outline-none focus:border-primary"
          placeholder="e.g., Mission District, San Francisco"
          data-testid="restaurant-location-input"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Cuisine Types *</label>
        <input
          type="text"
          value={cuisineTypes}
          onChange={(e) => setCuisineTypes(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-button focus:outline-none focus:border-primary"
          placeholder="e.g., Italian, Pizza, Casual Dining (comma separated)"
          data-testid="cuisine-types-input"
        />
        <p className="text-xs text-gray-500 mt-1">Separate multiple cuisines with commas</p>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Your Review *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-button focus:outline-none focus:border-primary h-32"
          placeholder="Tell us what you loved about this place..."
          data-testid="review-description-input"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Rating *</label>
        <div className="flex gap-3 flex-wrap">
          {Object.entries(RATING_EMOJIS).map(([value, { emoji, label }]) => (
            <button
              key={value}
              onClick={() => setRating(parseInt(value))}
              className={`flex flex-col items-center gap-1 p-3 border-2 rounded-button transition-all ${
                rating === parseInt(value) 
                  ? 'border-primary bg-primary/10' 
                  : 'border-gray-200 hover:border-primary'
              }`}
              data-testid={`rating-button-${value}`}
            >
              <span className="text-3xl">{emoji}</span>
              <span className="text-xs">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Insider Tips (Optional)</label>
        <textarea
          value={insiderTips}
          onChange={(e) => setInsiderTips(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-button focus:outline-none focus:border-primary h-24"
          placeholder="Share secret menu items, best times to visit, special tips only locals know..."
          data-testid="insider-tips-input"
        />
        <p className="text-xs text-gray-500 mt-1">💡 These tips are exclusive to your subscribers</p>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Who can see this review?</label>
        <div className="space-y-2">
          <label className={`flex items-start gap-3 p-3 border-2 rounded-button cursor-pointer transition-all ${
            visibility === 'premium' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary'
          }`}>
            <input
              type="radio"
              name="visibility"
              value="premium"
              checked={visibility === 'premium'}
              onChange={(e) => setVisibility(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="font-semibold text-sm">Premium Subscribers Only</p>
              <p className="text-xs text-gray-600">Only users who pay for your subscription can see the full review</p>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 border-2 rounded-button cursor-pointer transition-all ${
            visibility === 'free' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary'
          }`}>
            <input
              type="radio"
              name="visibility"
              value="free"
              checked={visibility === 'free'}
              onChange={(e) => setVisibility(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="font-semibold text-sm">Free Followers</p>
              <p className="text-xs text-gray-600">Free followers see limited content (3 lines, first photo only)</p>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 border-2 rounded-button cursor-pointer transition-all ${
            visibility === 'everyone' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary'
          }`}>
            <input
              type="radio"
              name="visibility"
              value="everyone"
              checked={visibility === 'everyone'}
              onChange={(e) => setVisibility(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="font-semibold text-sm">Everyone (Public)</p>
              <p className="text-xs text-gray-600">All subscribers see the full review regardless of tier</p>
            </div>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">Photos</label>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={onPhotoUpload}
          className="hidden"
          id="photo-upload"
          data-testid="photo-upload-input"
        />
        <label
          htmlFor="photo-upload"
          className="block w-full border-2 border-dashed border-gray-300 rounded-button p-8 text-center cursor-pointer hover:border-primary"
        >
          <span className="text-4xl mb-2 block">📸</span>
          <span className="text-sm text-gray-600">Tap to add photos</span>
        </label>
        
        {photos.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-600 mb-2">Click on a photo to add dish details</p>
            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo, index) => {
                const hasDishNotes = dishes.some(d => d.photo_index === index && (d.dish_name || d.notes));
                const isSelected = selectedPhotoIndex === index;
                
                return (
                  <div 
                    key={index} 
                    className="relative group"
                  >
                    <div
                      onClick={() => onSelectPhoto(index)}
                      className={`relative w-full h-32 rounded-button overflow-hidden cursor-pointer transition-all ${
                        isSelected ? 'ring-4 ring-primary' : 'hover:ring-2 hover:ring-primary'
                      }`}
                      data-testid={`uploaded-photo-${index}`}
                    >
                      <img
                        src={photo}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {hasDishNotes && (
                        <div className="absolute top-1 right-1 bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                          ✓
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <span className="text-white font-bold text-sm bg-primary px-2 py-1 rounded">Selected</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemovePhoto(index);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${index}`}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Dish Notes for Selected Photo */}
      {selectedPhotoIndex !== null && (
        <div className="bg-primary/10 border-2 border-primary rounded-button p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-semibold text-primary">
              Dish Details for Photo {selectedPhotoIndex + 1}
            </label>
            <button
              onClick={() => setSelectedPhotoIndex(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          <input
            type="text"
            value={dishes.find(d => d.photo_index === selectedPhotoIndex)?.dish_name || ''}
            onChange={(e) => onDishChange(selectedPhotoIndex, 'dish_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-button mb-2 focus:outline-none focus:border-primary"
            placeholder="Dish name (e.g., Har Gow Dumplings)"
            data-testid={`dish-name-input-${selectedPhotoIndex}`}
          />
          <textarea
            value={dishes.find(d => d.photo_index === selectedPhotoIndex)?.notes || ''}
            onChange={(e) => onDishChange(selectedPhotoIndex, 'notes', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-button focus:outline-none focus:border-primary"
            placeholder="Your notes about this dish... (e.g., Best in the city! Perfectly steamed)"
            rows="3"
            data-testid={`dish-notes-input-${selectedPhotoIndex}`}
          />
          <p className="text-xs text-gray-600 mt-2">💡 These details will appear when viewers click on this photo</p>
        </div>
      )}

      <button
        onClick={onSubmitReview}
        disabled={loading}
        className="w-full bg-primary text-white py-4 rounded-button font-bold text-lg hover:shadow-hover disabled:opacity-50"
        data-testid="submit-review-button"
      >
        {loading ? 'Posting...' : 'Share Review 🎉'}
      </button>
    </div>
  );
}

export default CreateReviewView;
